import {
    type ComponentType,
    createContext,
    useContext,
    useEffect,
    type FC
} from "react";
import type {
    AccordionItemPartContentProps,
    AccordionPartContentProps,
    AccordionItemContextType,
    AccordionContextType,
    AccordionLayoutType,
    AccordionPartKind
} from "./type";
import {
    warnOnce
} from "../../utils/development";

export const ACCORDION_PART_NAMES: Record<AccordionPartKind, string> = {
    content: "Content",
    trigger: "Trigger",
    item: "Item"
};

export const ACCORDION_DEFAULT_LAYOUT: AccordionLayoutType = {
    isFirst: false,
    isLast: false
};

export const AccordionContext = createContext<AccordionContextType | undefined>(undefined);

export const AccordionItemContext = createContext<AccordionItemContextType | undefined>(undefined);

export const AccordionLayoutContext = createContext<AccordionLayoutType>(ACCORDION_DEFAULT_LAYOUT);

export const useAccordionContext = ({
    part
}: {
    part: AccordionPartKind;
}): AccordionContextType | undefined => {
    const context = useContext(AccordionContext);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: `Accordion.${ACCORDION_PART_NAMES[part]}: must be rendered inside <Accordion>.`
        });
    }, [
        context,
        part
    ]);

    return context;
};

export const useAccordionItemContext = ({
    part
}: {
    part: AccordionPartKind;
}): AccordionItemContextType | undefined => {
    const itemContext = useContext(AccordionItemContext);

    useEffect(() => {
        if(itemContext) {
            return;
        }

        warnOnce({
            message: `Accordion.${ACCORDION_PART_NAMES[part]}: must be rendered inside <Accordion.Item>.`
        });
    }, [
        itemContext,
        part
    ]);

    return itemContext;
};

export const withAccordionContext = <P extends object>(
    part: AccordionPartKind,
    Content: ComponentType<AccordionPartContentProps<P>>
): FC<P> & {
    accordionPart: AccordionPartKind;
} => {
    const AccordionPartBase: FC<P> = (props) => {
        const context = useAccordionContext({
            part
        });

        if(!context) {
            return null;
        }

        return <Content
            {...props}
            context={context}
        />;
    };

    AccordionPartBase.displayName = `Accordion.${ACCORDION_PART_NAMES[part]}`;

    return Object.assign(AccordionPartBase, {
        accordionPart: part
    });
};

export const withAccordionItemContext = <P extends object>(
    part: AccordionPartKind,
    Content: ComponentType<AccordionItemPartContentProps<P>>
): FC<P> & {
    accordionPart: AccordionPartKind;
} => {
    const AccordionItemPartBase: FC<P> = (props) => {
        const context = useAccordionContext({
            part
        });

        const itemContext = useAccordionItemContext({
            part
        });

        if(!context || !itemContext) {
            return null;
        }

        return <Content
            {...props}
            itemContext={itemContext}
            context={context}
        />;
    };

    AccordionItemPartBase.displayName = `Accordion.${ACCORDION_PART_NAMES[part]}`;

    return Object.assign(AccordionItemPartBase, {
        accordionPart: part
    });
};
